<template>
  <div style="position: relative; width: 100%; height: 100%;">
    <canvas id="n3-chart"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
  name: 'n3-chart',
  props: {
    chartData: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      chartObj: null,
      n3ChartData: {
        type: 'bar',
        data: {
          labels: [],
          datasets: [
            {
              type: 'bar',
              label: 'Importe (€)',
              data: [],
              backgroundColor: '#1976D2',
              borderRadius: 4,
              yAxisID: 'y',
            },
            {
              type: 'line',
              label: 'Sol. Aprobadas',
              data: [],
              borderColor: '#4CAF50',
              borderWidth: 2,
              tension: 0.3,
              fill: false,
              yAxisID: 'y1',
            },
            {
              type: 'line',
              label: 'Sol. Pendientes',
              data: [],
              borderColor: '#FB8C00',
              borderWidth: 2,
              tension: 0.3,
              borderDash: [5, 5],
              fill: false,
              yAxisID: 'y1',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                boxWidth: 12,
                color: '#666666',
              },
            },
            tooltip: {
              mode: 'index',
              intersect: false,
            },
          },
          scales: {
            x: {
              ticks: { color: '#666666' },
              grid: { display: false },
            },
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              beginAtZero: true,
              ticks: {
                color: '#666666',
                callback(value) {
                  return `${value} €`;
                },
              },
              grid: { color: 'rgba(0, 0, 0, 0.1)' },
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              beginAtZero: true,
              ticks: {
                precision: 0,
                color: '#666666',
              },
              grid: {
                drawOnChartArea: false,
              },
            },
          },
        },
      },
    };
  },

  mounted() {
    this.applyThemeToChart();
    const ctx = document.getElementById('n3-chart');
    if (ctx) {
      // eslint-disable-next-line no-new
      this.chartObj = new Chart(ctx, this.n3ChartData);
    } else {
      console.error('Canvas element #n3-chart not found on mount');
    }
  },

  watch: {
    chartData: {
      handler(newVal) {
        if (!newVal) return;
        this.$nextTick(() => {
          this.updateChartData(newVal);
        });
      },
      deep: true,
      immediate: true,
    },
    '$vuetify.theme.dark': {
      handler() {
        if (this.chartObj) {
          this.applyThemeToChart();
          this.chartObj.update();
        }
      },
    },
  },

  methods: {
    hexToRgba(hex, alpha) {
      if (!hex || !hex.startsWith('#')) return `rgba(128, 128, 128, ${alpha})`;
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
      if (!result) return `rgba(128, 128, 128, ${alpha})`;
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    applyThemeToChart() {
      const isDark = this.$vuetify.theme.dark;
      const themeConfig = this.$vuetify.theme.themes ? this.$vuetify.theme.themes[isDark ? 'dark' : 'light'] : {};

      let primaryColor = themeConfig.primary || '#1976D2';
      if (typeof primaryColor === 'object') primaryColor = primaryColor.base || '#1976D2';

      let secondaryColor = themeConfig.secondary || '#424242';
      if (typeof secondaryColor === 'object') secondaryColor = secondaryColor.base || '#424242';

      let successColor = themeConfig.success || '#4CAF50';
      if (typeof successColor === 'object') successColor = successColor.base || '#4CAF50';

      let warningColor = themeConfig.warning || '#FB8C00';
      if (typeof warningColor === 'object') warningColor = warningColor.base || '#FB8C00';

      const textColor = isDark ? '#e0e0e0' : '#666666';
      const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';

      // Colors for datasets
      this.n3ChartData.data.datasets[0].backgroundColor = primaryColor;
      this.n3ChartData.data.datasets[1].borderColor = successColor;
      this.n3ChartData.data.datasets[2].borderColor = warningColor;

      // Colors for options
      this.n3ChartData.options.plugins.legend.labels.color = textColor;
      this.n3ChartData.options.scales.x.ticks.color = textColor;
      this.n3ChartData.options.scales.y.ticks.color = textColor;
      this.n3ChartData.options.scales.y.grid.color = gridColor;
      this.n3ChartData.options.scales.y1.ticks.color = textColor;
    },
    updateChartData(temporalData) {
      console.log('N3Chart updateChartData:', temporalData);
      if (!this.chartObj) {
        console.warn('N3Chart: chartObj not initialized yet');
        return;
      }
      if (!temporalData || temporalData.length === 0) {
        this.chartObj.data.labels = [];
        this.chartObj.data.datasets[0].data = [];
        this.chartObj.data.datasets[1].data = [];
        this.chartObj.data.datasets[2].data = [];
        this.chartObj.update();
        return;
      }

      const labels = temporalData.map((d) => d.monthName);
      const amounts = temporalData.map((d) => parseFloat(d.totalAmount));
      const approvedCounts = temporalData.map((d) => d.approvedCount);
      const pendingCounts = temporalData.map((d) => d.pendingCount);

      this.chartObj.data.labels = labels;
      this.chartObj.data.datasets[0].data = amounts;
      this.chartObj.data.datasets[1].data = approvedCounts;
      this.chartObj.data.datasets[2].data = pendingCounts;

      this.chartObj.update();
    },
  },
};
</script>
